import { useField } from "formik";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import {
  CloseCircleIcon,
  DeleteLineIcon,
  LoadingIcon,
  TickBoldIcon,
  UploadAddIcon,
} from "../../../assets/icons";
import fileIconPdf from "../../../assets/images/file-icon-pdf.png";
import humanizeFileSize from "../../../util/humanizeFileSize";
import useFileService from "../../../services/api/fileService";
import "../Forms.scss";

/**
 * FileCard component. This only works with `DropArea` so avoid using outside the `DropArea` component.
 * @param {{
 *   name: string,
 *   size: number,
 *   uploadedSize: number,
 *   onRemove: () => void
 * }} props
 * @returns {JSX.Element} FileCard
 */
function FileCard({ name, size, uploadedSize, onRemove }) {
  const fileType = name ? name.split(".").pop() : "unknown";

  return (
    <div className="file-card">
      <div className="content">
        <img src={fileIconPdf} alt={`file icon ${fileType}`} />

        <div className="meta">
          <p>{name}</p>

          <div className="upload-status">
            <span>
              {humanizeFileSize(uploadedSize)} of {humanizeFileSize(size)}
            </span>
            <div className="dot"></div>
            <div className="status-text">
              {size === uploadedSize ? (
                <TickBoldIcon className="success" />
              ) : (
                <LoadingIcon />
              )}
              <span>
                {size === uploadedSize ? "Completed" : "Uploading..."}
              </span>
            </div>
          </div>
        </div>

        <div className="file-action">
          {size === uploadedSize ? (
            <button type="button" onClick={onRemove}>
              <DeleteLineIcon />
            </button>
          ) : (
            <button type="button" onClick={onRemove}>
              <CloseCircleIcon />
            </button>
          )}
        </div>
      </div>

      {size !== uploadedSize && (
        <div className="progress">
          <div
            className="bar"
            style={{ width: `${(uploadedSize / size) * 100}%` }}
          ></div>
        </div>
      )}
    </div>
  );
}

/**
 * DropArea component
 * @param {{
 *   name: string,
 *   label?: string,
 *   className?: string,
 *   bottomText?: string,
 *   maxFiles?: number,
 *   maxSizePerFile?: number,
 *   isFileUpdate?: boolean,
 *   fileId?: string
 * }} props
 * @returns
 */
function DropArea({
  name,
  label,
  className = "",
  bottomText,
  maxSizePerFile = 5242880,
  maxFiles = 1,
  isFileUpdate = false,
  fileId,
}) {
  const [field, meta, helpers] = useField(name);
  const [uploadError, setUploadError] = useState(undefined);
  const [fileList, setFileList] = useState([]);
  const { setValue, setTouched } = helpers;

  const inputRef = useRef(null);
  const {
    uploadSingleFile,
    uploadMultipleFiles,
    deleteFile,
    getFileDetails,
    queryKey,
  } = useFileService();

  const { data: filePayload } = useQuery({
    queryKey: [queryKey, fileId],
    queryFn: () => getFileDetails(fileId),
    enabled: isFileUpdate && !!fileId,
  });

  useEffect(() => {
    // Check if we have data. The structure seems to be response.data.payload.
    // Based on user edit, they expected payload.file.
    // We'll safely check for filePayload.file or just filePayload if it's the object.
    const fileDetails = filePayload?.file || filePayload;

    if (fileDetails) {
      setValue(fileId);
      setFileList([
        {
          name:
            fileDetails.originalName ||
            fileDetails.fileName ||
            fileDetails.name ||
            "Unknown File",
          size: fileDetails.fileSize || 0,
          uploadedSize: fileDetails.fileSize || 0,
          id: fileDetails._id,
          status: "completed",
        },
      ]);
    }
  }, [filePayload, fileId, isFileUpdate, setValue]);

  const uploadMutation = useMutation({
    mutationFn: (files) => {
      if (maxFiles === 1) {
        return uploadSingleFile(files[0]);
      }
      return uploadMultipleFiles(Array.from(files));
    },
    onSuccess: (data) => {
      if (maxFiles === 1) {
        const fileData = data?.file;
        if (fileData) {
          setValue(fileData._id);
          setFileList([
            {
              name:
                fileData.originalName ||
                fileData.fileName ||
                fileData.name ||
                "Unknown File",
              size: fileData.fileSize || fileData.size || 0,
              uploadedSize: fileData.fileSize || fileData.size || 0,
              id: fileData._id,
              status: "completed",
            },
          ]);
        }
      } else {
        const filesData = data?.files;
        if (filesData) {
          setValue(filesData.map((f) => f._id));
          setFileList(
            filesData.map((f) => ({
              name: f.originalName,
              size: f.fileSize,
              uploadedSize: f.fileSize,
              id: f._id,
              status: "completed",
            })),
          );
        }
      }
    },
    onError: (err) => {
      setUploadError("Upload failed. Please try again.");
      setFileList([]);
      setValue(maxFiles === 1 ? "" : []);
      console.error(err);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteFile,
    onError: (err) => {
      console.error("Failed to delete file from server", err);
    },
  });

  function openPicker() {
    inputRef.current?.showPicker();
  }

  function handleField(files) {
    setTouched(true);
    setUploadError(undefined);

    if (files.length === 0) return;

    // Check if updating and there's an existing file, try to delete it first
    if (isFileUpdate && maxFiles === 1 && field.value) {
      deleteMutation.mutate(field.value);
    }

    if (maxFiles === 1) {
      if (files[0].size > maxSizePerFile) {
        setUploadError(
          `Maximum file size is ${humanizeFileSize(maxSizePerFile)}`,
        );
        return;
      }
      setFileList([
        {
          name: files[0].name,
          size: files[0].size,
          uploadedSize: 0,
          status: "uploading",
        },
      ]);
    } else {
      if (files.length > maxFiles) {
        setUploadError(`Maximum ${maxFiles} files are allowed`);
        return;
      }
      if ([...files].some((file) => file.size > maxSizePerFile)) {
        setUploadError(
          `Maximum file size is ${humanizeFileSize(maxSizePerFile)}`,
        );
        return;
      }
      setFileList(
        Array.from(files).map((f) => ({
          name: f.name,
          size: f.size,
          uploadedSize: 0,
          status: "uploading",
        })),
      );
    }

    uploadMutation.mutate(files);
  }

  function handleRemove(index) {
    const fileToRemove = fileList[index];

    const newList = [...fileList];
    newList.splice(index, 1);
    setFileList(newList);

    // Call API to delete if it has an ID
    if (fileToRemove.id) {
      deleteMutation.mutate(fileToRemove.id);
    }

    // Update Formik state
    if (maxFiles === 1) {
      setValue("");
    } else {
      if (field.value && Array.isArray(field.value)) {
        const newValue = field.value.filter((id) => id !== fileToRemove.id);
        setValue(newValue);
      }
    }

    // Reset input to allow re-selection
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  }

  function handleDragOver(e) {
    e.preventDefault();
  }

  function handleDrop(e) {
    e.preventDefault();
    if (!uploadMutation.isPending) {
      handleField(e.dataTransfer.files);
    }
  }

  function handleChange(e) {
    handleField(e.target.files);
  }

  return (
    <div className={`input ${className}`}>
      {label ? <label htmlFor={name}>{label}</label> : null}

      <div className="drop-area-wrapper">
        <div
          className="drop-area-field"
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <input
            name={name}
            type="file"
            ref={inputRef}
            multiple={maxFiles > 1}
            onChange={handleChange}
            style={{ display: "none" }}
          />

          <UploadAddIcon />

          <p>Choose a file or drag & drop it here</p>
          <small>JPEG, PNG and PDF formats, up to 20MB</small>

          <button
            type="button"
            className="browse-btn"
            onClick={openPicker}
            disabled={uploadMutation.isPending}
          >
            {uploadMutation.isPending ? "Uploading..." : "Browse File"}
          </button>
        </div>

        <div className="meta">
          {meta.touched && (meta.error || uploadError) ? (
            <small className="error-msg">{uploadError || meta.error}</small>
          ) : null}
          {bottomText ? <small>{bottomText}</small> : null}
        </div>

        <div className="drop-area-selected-files">
          {fileList.map((f, k) => (
            <FileCard
              key={k}
              name={f.name}
              size={f.size}
              uploadedSize={f.uploadedSize}
              onRemove={() => handleRemove(k)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default DropArea;
